import {Injectable} from '@angular/core';
import {QuoteService} from '../models/quote/quote-service';
import {EnumCorridorColor} from '../models/enum/enum-corridor-color';
import Big from 'big.js';
import {bigToString} from '../utils/math-utils';
import {Coefficient, MultiServiceBoost, PctCalculator, QuoteServicePct} from 'rem-pct-engine-core';
import {QuoteServiceManager, QuoteWrapperManager, ServiceLineManager} from 'quote-engine-core';

import {PctConfigModel} from '../models/pct-config/pct-config.model';
import {ServiceConfig} from '../models/referential/service-config.model';
import {QuoteWrapper} from '../models/quote/quote-wrapper';
import {EnumQuoteServiceSalesType} from '../models/enum/enum-quote-service-sales-type';
import {CmcQuote} from '../models/quote/cmc-quote';
import {EmployeeRoleEnum} from '../models/employee/employee-role.enum';
import {ServiceLine} from '../models/quote/service-line';
import {QuoteEnginePrototypeMapper} from '../utils/QuoteEnginePrototypeMapper';
import {EPPClientPrototypeMapper} from '../utils/EPPClientPrototypeMapper';
import {EPPClientPrototypeFromQuoteEngineMapper} from '../utils/EPPClientPrototypeFromQuoteEngineMapper';
import {CustomGroup} from '../models/quote/group/custom-group';
import {PricingItem} from '../models/quote/pricing-item/pricing-item';
import {OptionPricingItem} from '../models/quote/pricing-item/option-pricing-item';
import {OptionPricingItemMapper} from '../models/quote/pricing-item/mapper/option-pricing-item.mapper';
import {EnumPricingItemType} from '../models/quote/pricing-item/pricing-item-type';
import {Option} from '../models/option/option.model';
import {EnumOptionGranularity} from '../models/enum/enum.option-granularity';

@Injectable({
    providedIn: 'root'
})
export class QuoteServiceService {


    constructor(
        private pctCalculator: PctCalculator,
        private quoteServiceManager: QuoteServiceManager,
        private quoteWrapperManager: QuoteWrapperManager,
        private serviceLineManager: ServiceLineManager,
        private optionPricingItemMapper: OptionPricingItemMapper) {
    }

    updateQuoteWrapperColors(quoteWrapper: QuoteWrapper): QuoteWrapper {

        let updatedQuoteWrapper = this.quoteWrapperManager.updateQuoteWrapperColors(QuoteEnginePrototypeMapper.setQuoteWrapperPrototype(quoteWrapper));

        quoteWrapper = quoteWrapper.copyWith({
            color: EnumCorridorColor[updatedQuoteWrapper.color],
            existingColor: EnumCorridorColor[updatedQuoteWrapper.existingColor]
        });
        return quoteWrapper;
    }

    calculateCorridors(quoteService: QuoteService, referential: any, referentialService: any, countryIsoCode: string, employeeRole: EmployeeRoleEnum): QuoteService {
        let coreQuoteService = this.quoteServiceManager.calculateCorridors(QuoteEnginePrototypeMapper.setQuoteServicePrototype(quoteService), referential, [referentialService], countryIsoCode, employeeRole.pricingRole);
        return EPPClientPrototypeFromQuoteEngineMapper.setQuoteServicePrototype(coreQuoteService);
    }

    updateServiceLine(serviceLine: ServiceLine) {
        let coreServiceLine = this.serviceLineManager.updateServiceLineAppliedPriceAndColor(QuoteEnginePrototypeMapper.setServiceLinePrototype(serviceLine));

        return EPPClientPrototypeFromQuoteEngineMapper.setServiceLinePrototype(coreServiceLine);
    }

    calculateAdditionalDetails(quoteService: QuoteService): QuoteService {


        let updateQuoteService = this.quoteServiceManager.calculateExtraFeesQtyAndAmount(QuoteEnginePrototypeMapper.setQuoteServicePrototype(quoteService));
        updateQuoteService = this.quoteServiceManager.calculateAdditionalStockAmount(updateQuoteService);
        updateQuoteService = this.quoteServiceManager.calculateCustomGroupTotalAppliedPrice(updateQuoteService);

        return EPPClientPrototypeFromQuoteEngineMapper.setQuoteServicePrototype(updateQuoteService);
    }

    mapServiceConfigToServiceLine(serviceConfig: ServiceConfig, salesType: EnumQuoteServiceSalesType): ServiceLine {
        return new ServiceLine().copyWith({
            code: serviceConfig.serviceCode,
            newQuoteService: this.mapServiceConfigToQuoteService(serviceConfig, salesType),
            isSelected: false,
            appliedPriceColor: EnumCorridorColor.GRAY,
            appliedPrice: '0.00',
            isEnabled: serviceConfig.isEnabled,
            order: serviceConfig.order
        });
    }

    public mapServiceConfigToQuoteService(serviceConfig: ServiceConfig, salesType: EnumQuoteServiceSalesType): QuoteService {
        let filteredContractInfoInputs =
            serviceConfig.parameters.filter(param => param.key === 'contractInfoInputs');

        let filteredDisplayMode =
            serviceConfig.parameters.filter(param => param.key === 'displayMode');
        return new QuoteService().copyWith({
            code: serviceConfig.serviceCode,
            customGroups: [],
            isCustomGroups: false,
            isSelected: false,
            appliedPriceDevicesColor: EnumCorridorColor.GRAY,
            appliedPriceConsumablesColor: EnumCorridorColor.GRAY,
            appliedPrice: '0.00',
            pct: '0.00',
            averagePctCoefficient: '0.00',
            contractInfoInputs: filteredContractInfoInputs.length > 0 ? filteredContractInfoInputs[0].value : null,
            extraFees: [],
            displayMode: filteredDisplayMode.length > 0 ? filteredDisplayMode[0].value.displayMode : null,
            salesType: salesType
        });
    }


    updateServiceAppliedPrice(service: QuoteService, newAppliedPrice: string): QuoteService {
        let coreQuoteService = this.quoteServiceManager.updateServiceAppliedPrice(QuoteEnginePrototypeMapper.setQuoteServicePrototype(service), newAppliedPrice);
        return EPPClientPrototypeFromQuoteEngineMapper.setQuoteServicePrototype(coreQuoteService);
    }

    public initServiceLinesPctCoefficient(serviceLines: ServiceLine[], pctConfig: PctConfigModel): ServiceLine[] {
        const newQuotesLines = [];
        serviceLines.forEach(sl => {
            const coefIndex = pctConfig?.coefficients?.findIndex(c => c.serviceCode === sl.code);
            const coef = pctConfig?.coefficients[coefIndex];


            if (coef !== undefined) {
                newQuotesLines.push(sl.copyWith(
                    {
                        newQuoteService: sl.newQuoteService.copyWith({
                            nonNegociableArticleColor: pctConfig?.nonNegociableArticleColor,
                            uniqueReferenceCoefficient: pctConfig?.uniqueReference,
                            multiServiceBoost: this.getMultiServiceBoost(pctConfig, sl.newQuoteService),
                            coefficient:
                                new Coefficient(coef.serviceCode,
                                    coef.c10,
                                    coef.c10c50,
                                    coef.c50,
                                    coef.c50c90,
                                    coef.c90,
                                    coef.monthlyAmount)
                        })
                    }))
                    ;
            }

        });
        return newQuotesLines;
    }

    private getMultiServiceBoost(pctConfig: PctConfigModel, qs: QuoteService) {
        const multiServiceBoostIndex = pctConfig?.multiserviceBoosts?.findIndex(c => c.serviceCode === qs.code);
        if (multiServiceBoostIndex != undefined && multiServiceBoostIndex > -1) {
            const multiServiceBoost = pctConfig?.multiserviceBoosts[multiServiceBoostIndex];
            return new MultiServiceBoost(multiServiceBoost.serviceCode,
                multiServiceBoost.coeffOfConsideration,
                multiServiceBoost.minCaOfConsideration,
                multiServiceBoost.isImpacted,
                multiServiceBoost.minCaForImpact);
        }
        return null;
    }

    public initServiceLinesWithFlatCorridorColor(serviceLines: ServiceLine[], nonNegociableArticleColor: string): ServiceLine[] {

        let coreServiceLines = this.initServiceLinesServices(serviceLines, nonNegociableArticleColor);
        coreServiceLines.forEach(serviceLine =>
            EPPClientPrototypeMapper.setServiceLinePrototype(serviceLine)
        );
        return coreServiceLines;

    }

    /**
     * TO EXPORT
     */
    public initServiceLinesServices(serviceLines: ServiceLine[], nonNegociableArticleColor: string): ServiceLine[] {
        const newServiceLines: ServiceLine[] = [];
        serviceLines.forEach(sl => {

            newServiceLines.push(sl.copyWith({
                newQuoteService: sl.newQuoteService?.copyWith({ nonNegociableArticleColor: nonNegociableArticleColor }),
                renewalQuoteService: sl.renewalQuoteService?.copyWith({ nonNegociableArticleColor: nonNegociableArticleColor }),
                existingQuoteService: sl.existingQuoteService?.copyWith({ nonNegociableArticleColor: nonNegociableArticleColor })
            }));
        });
        return newServiceLines;
    }

    /**
     * END EXPORT
     */

    calculatePcts(quoteWrapper: QuoteWrapper, referentialServices: any[]): QuoteWrapper {
        let servicesConfPct: QuoteServicePct[] = [];
        const cmcUniqueReference = quoteWrapper instanceof CmcQuote ? quoteWrapper.cmcUniqueReference : false;
        quoteWrapper.serviceLines.forEach(serviceLine => {
            let contractDuration = 0;
            if (serviceLine.newQuoteService.contractInfoInputs) {
                contractDuration = parseFloat(serviceLine.newQuoteService.contractInfoInputs.contractDuration);
            }
            const referentialService = referentialServices?.find(referential => referential.serviceCode === serviceLine.code);
            const defaultContractDuration = parseFloat(referentialService?.contractInfoInputs?.contractDuration);
            const serviceConfPct = new QuoteServicePct(
                serviceLine.newQuoteService.code,
                serviceLine.newQuoteService.multiServiceBoost,
                serviceLine.newQuoteService.coefficient,
                serviceLine.newQuoteService.serviceColor,
                parseFloat(serviceLine.newQuoteService.monthlyAmount),
                parseFloat(serviceLine.newQuoteService.nonNegociableArticlesMonthlyAmount),
                parseFloat(serviceLine.newQuoteService.negociableArticlesMonthlyAmount),
                contractDuration,
                cmcUniqueReference,
                serviceLine.newQuoteService.nonNegociableArticleColor,
                parseFloat(serviceLine.newQuoteService.uniqueReferenceCoefficient),
                defaultContractDuration
            );
            servicesConfPct.push(serviceConfPct);
        });
        const pcts = this.pctCalculator.calculatePcts(servicesConfPct, quoteWrapper.coefficientBoosts);
        let serviceLines: ServiceLine[] = [];
        quoteWrapper.serviceLines.forEach(serviceLine => {
            const _pct = pcts.get(serviceLine.code);
            if (!!_pct) {
                const averagePctCoefficient = _pct.pct ? bigToString(new Big(_pct.pct).div(serviceLine.newQuoteService.monthlyAmount)) : '0.00';
                const deltaPctWithOneMoreService = _pct.deltaPctWithOneMoreService ? _pct.deltaPctWithOneMoreService + '' : '0';
                serviceLines.push(serviceLine.copyWith({
                    newQuoteService: serviceLine.newQuoteService.copyWith({
                        pct: '' + _pct.pct,
                        deltaPctWithOneMoreService: deltaPctWithOneMoreService,
                        averagePctCoefficient: averagePctCoefficient
                    })
                }));
            }
        });
        return quoteWrapper.copyWith({ serviceLines });
    }

    public synchronizeArticleOptions(quoteService: QuoteService, options: Option[]): QuoteService {
        const articleOptionsByCode = new Map<string, Option>(
            options.filter(option => option.granularity === EnumOptionGranularity.ARTICLE)
                .map(option => [option.code, option]));

        const isArticleOption = (pricingItem: PricingItem): pricingItem is OptionPricingItem =>
            pricingItem.pricingItemType === EnumPricingItemType.OPTION
            && (pricingItem as OptionPricingItem).granularity === EnumOptionGranularity.ARTICLE;

        const quantitiesByCode = new Map<string, number>();
        quoteService.customGroups.forEach(customGroup =>
            customGroup.pricingItems
                // Les lignes CMC n'ont pas d'options article : leurs quantités ne comptent pas
                .filter(pricingItem => pricingItem.pricingItemType !== EnumPricingItemType.OPTION && !pricingItem.isCmc)
                .forEach(pricingItem => {
                    const codes: string[] = pricingItem.userInputs?.['options'] ?? [];
                    codes.filter(code => articleOptionsByCode.has(code))
                        .forEach(code => quantitiesByCode.set(code,
                            (quantitiesByCode.get(code) ?? 0) + Number(pricingItem.userInputs?.['quantity'] ?? 0)));
                }));

        let hasChanged = false;
        const syncedCodes = new Set<string>();

        let newCustomGroups = quoteService.customGroups.map(customGroup => {
            let groupChanged = false;
            const newPricingItems = customGroup.pricingItems
                .filter(pricingItem => {
                    const isObsolete = isArticleOption(pricingItem) && !quantitiesByCode.has(pricingItem.option?.codeElis);
                    groupChanged = groupChanged || isObsolete;
                    return !isObsolete;
                })
                .map(pricingItem => {
                    if (isArticleOption(pricingItem)) {
                        syncedCodes.add(pricingItem.option?.codeElis);
                        const quantity = quantitiesByCode.get(pricingItem.option?.codeElis);
                        if (pricingItem.userInputs?.['quantity'] !== quantity) {
                            groupChanged = true;
                            return pricingItem.copyWith({ userInputs: { ...pricingItem.userInputs, quantity: quantity } });
                        }
                    }
                    return pricingItem;
                });
            if (!groupChanged) {
                return customGroup;
            }
            hasChanged = true;
            return new CustomGroup().copyWith({
                id: customGroup.id,
                title: customGroup.title,
                order: customGroup.order,
                pricingItems: newPricingItems,
                isDuplicated: customGroup.isDuplicated
            });
        });

        const missingOptions = [...quantitiesByCode.keys()]
            .filter(code => !syncedCodes.has(code))
            .map(code => this.optionPricingItemMapper.getPricingItemFromOption(
                articleOptionsByCode.get(code), quantitiesByCode.get(code)));

        if (missingOptions.length > 0) {
            hasChanged = true;
            const defaultCustomGroup = newCustomGroups[0];
            newCustomGroups = newCustomGroups.map((customGroup, index) =>
                index === 0
                    ? new CustomGroup().copyWith({
                        id: defaultCustomGroup.id,
                        title: defaultCustomGroup.title,
                        order: defaultCustomGroup.order,
                        pricingItems: [...defaultCustomGroup.pricingItems, ...missingOptions],
                        isDuplicated: defaultCustomGroup.isDuplicated
                    })
                    : customGroup);
        }

        return hasChanged ? quoteService.copyWith({customGroups: newCustomGroups}) : quoteService;
    }

    public synchronizeServiceOptions(quoteService: QuoteService, options: Option[], resetDefaultChecked?: boolean): QuoteService {


        const isServiceOption = (item: PricingItem): item is OptionPricingItem =>
            item.pricingItemType === EnumPricingItemType.OPTION
            && (item as OptionPricingItem).granularity === EnumOptionGranularity.SERVICE;

        const hasNonOptionItems = quoteService.customGroups
            .some(cg => cg.pricingItems.some(item => !isServiceOption(item) && item.pricingItemType !== EnumPricingItemType.OPTION && !item.isCmc));


        // Aucun PricingItem non-option hors CMC :
        // supprimer toutes les Service Options
        if (!hasNonOptionItems) {
            const newCustomGroups = quoteService.customGroups.map(cg => {
                const filtered = cg.pricingItems.filter(item => !isServiceOption(item));

                if (filtered.length === cg.pricingItems.length) {
                    return cg;
                }
                return new CustomGroup().copyWith({
                    id: cg.id,
                    title: cg.title,
                    order: cg.order,
                    pricingItems: filtered,
                    isDuplicated: cg.isDuplicated
                });
            });

            const changed = newCustomGroups.some(
                (cg, i) => cg !== quoteService.customGroups[i]
            );

            return changed
                ? quoteService.copyWith({ customGroups: newCustomGroups })
                : quoteService;
        }

        // Pas de reset => aucune synchronisation
        if (!resetDefaultChecked) {
            return quoteService;
        }

        const serviceOptions = options.filter(option =>
            option.granularity === EnumOptionGranularity.SERVICE && option.serviceCode === quoteService.code);

        const optionCodes = new Set(
            serviceOptions.map(option => option.code)
        );

        const existingServiceOptions = quoteService.customGroups
            .flatMap(cg => cg.pricingItems)
            .filter(isServiceOption);

        const existingOptionCodes = new Set(
            existingServiceOptions.map(item => item.option?.codeElis)
        );

        // Conserve uniquement les Service Options encore présentes dans la nouvelle liste (cas de duplication) 
        //TODO : Ce cas traite aussi le cas des options non par défaut
        const synchronizedOptions = existingServiceOptions.filter(
            item => optionCodes.has(item.option?.codeElis ?? '')
        );

        // Ajoute les nouvelles Service Options
        const defaultCheckedServiceOptions = serviceOptions.filter(option => option.defaultChecked);
        const newOptions = defaultCheckedServiceOptions
            .filter(option => !existingOptionCodes.has(option.code))
            .map(option =>
                this.optionPricingItemMapper
                    .getPricingItemFromOption(option, 0)
                    .copyWith({
                        granularity: EnumOptionGranularity.SERVICE
                    })
            );

        const synchronizedAndNewOptions = [
            ...synchronizedOptions,
            ...newOptions
        ];

        const firstGroup = quoteService.customGroups[0];

        // Tous les PricingItems normaux sont conservés.
        // Les Service Options sont remplacées par la liste synchronisée.
        const nonServiceOptionItems = firstGroup.pricingItems.filter(
            item => !isServiceOption(item)
        );

        const newPricingItems = [
            ...nonServiceOptionItems,
            ...synchronizedAndNewOptions
        ];

        const newCustomGroups = quoteService.customGroups.map((cg, index) =>
            index === 0
                ? new CustomGroup().copyWith({
                    id: firstGroup.id,
                    title: firstGroup.title,
                    order: firstGroup.order,
                    pricingItems: newPricingItems,
                    isDuplicated: firstGroup.isDuplicated
                })
                : cg
        );

        return quoteService.copyWith({
            customGroups: newCustomGroups
        });
    }



}
